import React from 'react';
import i18n from 'util/i18n';
import { Summary, DateSummary } from 'components/Summary';
import { SUBSTANCE_USE, SUBSTANCE_USE_ALCOHOL_SUPPLEMENTAL } from 'config/formSections/substanceUse';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { alcoholSupplementalCounselingAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Comments from 'components/Form/Comments';
import Field from 'components/Form/Field';
import AlcoholCounseling from './AlcoholCounseling';

const sectionConfig = {
  key: SUBSTANCE_USE_ALCOHOL_SUPPLEMENTAL.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_ALCOHOL_SUPPLEMENTAL.name,
  storeKey: SUBSTANCE_USE_ALCOHOL_SUPPLEMENTAL.storeKey,
};

export class AlcoholSupplemental extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateAlcoholTreatmentOrCounselingAnswer = this.updateAlcoholTreatmentOrCounselingAnswer.bind(this);
    this.updateAlcoholTreatmentOrCounselingComment = this.updateAlcoholTreatmentOrCounselingComment.bind(this);
    this.updateAlcoholTreatmentOrCounselingSummaryComment = this.updateAlcoholTreatmentOrCounselingSummaryComment.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      AlcoholTreatmentOrCounselingAnswer: this.props.AlcoholTreatmentOrCounselingAnswer,
      AlcoholTreatmentOrCounselingComment: this.props.AlcoholTreatmentOrCounselingComment,
      AlcoholTreatmentOrCounselingSummaryComment: this.props.AlcoholTreatmentOrCounselingSummaryComment,
      List: this.props.List,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateAlcoholTreatmentOrCounselingAnswer = (values) => {
    this.update({
      AlcoholTreatmentOrCounselingAnswer: values,
      List: values.value === 'Yes' ? this.props.List : { items: [], branch: {} },
    });
  };

  updateAlcoholTreatmentOrCounselingComment = (values) => {
    this.update({
      AlcoholTreatmentOrCounselingComment: values,
    });
  };

  updateAlcoholTreatmentOrCounselingSummaryComment = (values) => {
    this.update({
      AlcoholTreatmentOrCounselingSummaryComment: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const counselor = o.TreatmentProviderName ? o.TreatmentProviderName.value : '';
    const counselingDates = DateSummary(o.CounselingDates);

    return Summary({
      type: i18n.t('substance.alcohol.anyCounseling.collection.itemType'),
      index,
      left: counselor,
      right: counselingDates,
      placeholder: i18n.t('substance.alcohol.anyCounseling.collection.summary'),
    });
  };

  render() {
    const { errors, peopleWhoKnowYou, List } = this.props;
    const branchLabelCopy = i18n.t('substance.alcohol.heading.anyCounseling');
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, alcoholSupplementalCounselingAddressNameFields);
    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return (
      <div
        className="section-content any-counselings"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_ALCOHOL_SUPPLEMENTAL.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.alcohol.any')}</h1>
        <Branch
          attribute="AlcoholTreatmentOrCounselingAnswer"
          name="AlcoholTreatmentOrCounselingAnswer"
          label={branchLabelCopy}
          labelSize="h4"
          className="treatment-or-counseling-answer"
          {...this.props.AlcoholTreatmentOrCounselingAnswer}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateAlcoholTreatmentOrCounselingAnswer}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.alcohol.anyCounseling.heading.alcoholTreatmentOrCounselingComment')}
          name="AlcoholTreatmentOrCounselingComment"
          className="counseling-comment"
          attribute="AlcoholTreatmentOrCounselingComment"
          {...this.props.AlcoholTreatmentOrCounselingComment}
          onUpdate={this.updateAlcoholTreatmentOrCounselingComment}
          required={false}
        />

        <Show when={this.props.AlcoholTreatmentOrCounselingAnswer.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.alcohol.anyCounseling.collection.description')}
            appendTitle={i18n.t('substance.alcohol.anyCounseling.collection.appendTitle')}
            appendLabel={i18n.t('substance.alcohol.anyCounseling.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <AlcoholCounseling
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.alcohol.anyCounseling.heading.counselingSummaryComment')}
            name="AlcoholTreatmentOrCounselingSummaryComment"
            className="counseling-summary-comment"
            attribute="AlcoholTreatmentOrCounselingSummaryComment"
            {...this.props.AlcoholTreatmentOrCounselingSummaryComment}
            onUpdate={this.updateAlcoholTreatmentOrCounselingSummaryComment}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

AlcoholSupplemental.defaultProps = {
  AlcoholTreatmentOrCounselingAnswer: {},
  AlcoholTreatmentOrCounselingComment: {},
  AlcoholTreatmentOrCounselingSummaryComment: {},
  List: EmptyListInstance,
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'alcohol/supplemental',
  addressBooks: {},
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(AlcoholSupplemental, sectionConfig);
